import {
  formatRoute,
  queueCurrent,
  queueEntryGone,
  queueNeighbours,
  type QueueEntry,
  type QueueLink,
  type QueueState,
  type QueueVideo,
} from '@visious/core';
import {
  isWatchedVideo,
  progressMarker,
  toListingProgress,
  type ListingBadge,
  type ProgressLookup,
} from '../listing/model';
import type { MiniNotice, WatchPlaylistRow } from '../screens/watch/model';
import type { WatchTarget } from '../screens/watch/useWatchScreen';
import { formatDuration } from '../format';
import { text } from '../strings';

/**
 * The queue as the rest of the app sees it (R13): rows, routes and targets, no DOM and no React.
 *
 * The whole of what a queue row draws is carried in the row itself — title, channel, picture,
 * duration — so **adding a video to the queue costs no request**. That is not an optimisation, it is
 * what makes the "Add to queue" button cheap enough to put on every card in a grid: the card already
 * has every field a queue entry needs (`listing/model.ts`), so forty buttons on a page cost nothing
 * to draw and nothing to press.
 */

/**
 * What the queue's two controls need to know about a video: the row they would make of it, plus
 * where the video lives.
 *
 * Narrower than `ListingItem` on purpose. Three grids draw videos in three row types — the feed's
 * `ListingItem`, the playlist page's entry and the history page's row — and none of the three is
 * assignable to either of the others. What they *do* agree on is exactly this, which is the whole of
 * what a queue entry and a press need, so one type serves all three and no page has to be converted
 * into another page's shape to gain a queue button.
 */
export interface QueueCandidate {
  readonly videoId: string;
  /** The video's own route, which is what **Play now** opens — a playlist entry keeps its `list`. */
  readonly href: string;
  readonly title: string;
  readonly author: string | null;
  readonly authorHref: string | null;
  readonly thumbnailUrl: string | null;
  readonly durationSec: number | null;
}

/**
 * A card as a queue entry. The one conversion between the two, so a field the queue keeps is
 * decided here and nowhere else.
 *
 * The **badge is deliberately dropped** and the duration kept: a badge is a fact about the record at
 * the moment it was listed ("LIVE", "PREMIERES 8 PM"), and a queue outlives that moment by hours. A
 * duration is a property of the video. The row rebuilds its badge from the duration when it draws,
 * so a queued premiere shows no badge rather than a stale promise.
 *
 * The `href` is dropped too, and that is not the same kind of decision: a queue row's route is built
 * from the video id alone ({@link queueEntryHref}), so keeping the card's own would put a `list=` in
 * a queue.
 */
export function toQueueVideo(item: QueueCandidate): QueueVideo {
  return {
    videoId: item.videoId,
    // The feed's rows use `''` for a video with no channel; the other two already use `null`.
    author: item.author === '' ? null : item.author,
    title: item.title,
    authorHref: item.authorHref,
    thumbnailUrl: item.thumbnailUrl,
    durationSec: item.durationSec,
  };
}

/**
 * Where the corner player goes when the queue is what is driving it.
 *
 * **A list of the reader's own carries no `list` and no `index`.** It is not a playlist on the
 * instance, so there is nothing to put in `list=` that anyone else could read — the address bar says
 * only which video is playing, and which queue row that is stays in this tab. That also keeps the
 * Watch-on and Share links honest, since both are built from the route and would otherwise be handing
 * out a playlist id that exists nowhere ([urls.md](../../../../docs/specs/urls.md): a `?list=` that
 * means nothing to the destination is worse than no `list` at all).
 *
 * **A linked row carries both**, because there the queue *is* a playlist on the instance: Maximize
 * takes the reader back into the list rather than dropping them out of it, and both ids are real
 * everywhere the route is read.
 *
 * Never a `t` either way. The corner player's position is not something the cursor knows, and a route
 * that invented one would restart the video it names.
 */
export function queueWatchTarget(state: QueueState): WatchTarget | null {
  const entry = queueCurrent(state);
  if (entry === null) return null;
  const linked = state.link !== null && entry.playlistIndex !== null;
  return {
    videoId: entry.videoId,
    startSec: null,
    playlistId: linked ? (state.link?.playlistId ?? null) : null,
    index: linked ? entry.playlistIndex : null,
  };
}

/**
 * What the corner says once the queue has played its list out (`playback/QueueEnded.tsx`).
 *
 * A notice like any the corner player puts up, so it travels the same way: the sentence together
 * with what kind of statement it is, since below the `md` step the corner draws the kind as a glyph
 * and a finished string has nothing in it to pick one from (`screens/watch/model.ts`).
 */
export function queueEndedNotice(): MiniNotice {
  return { text: text().queue.ended.line, kind: 'ended' };
}

/**
 * The row the box that says the queue has ended acts on: the **last** one.
 *
 * The end of a queue is the cursor being off the list, so there is no neighbour to ask for — "the
 * previous video" is the row the list finished on, and that is the only reading of either control that
 * goes back into the list from there. The step back plays it in the corner and Maximize opens its watch
 * page, and they agree because both ask this. `null` for a queue with no rows, which is not a state the
 * box is drawn in (`playback/PlaybackHost.tsx` requires entries).
 */
export function queueLastEntry(state: QueueState): QueueEntry | null {
  return state.entries[state.entries.length - 1] ?? null;
}

/**
 * The in-app route for a queue entry, which is the route of whichever list it belongs to.
 *
 * A row of a **linked** playlist keeps that playlist's context — `v`, `list` and `index` — so a press
 * on it is the same address the playlist page would have sent the reader to, and the arrival keeps the
 * list rather than discarding it (`queue/useQueueAdvance.ts`). A row of the queue's **own** is a plain
 * `/watch?v=`; see {@link queueWatchTarget} for why it can be nothing else.
 */
export function queueEntryHref(entry: QueueEntry, link: QueueLink | null): string {
  if (link === null || entry.playlistIndex === null) return queueVideoHref(entry.videoId);
  return formatRoute({
    kind: 'watch',
    videoId: entry.videoId,
    startSec: null,
    playlistId: link.playlistId,
    index: entry.playlistIndex,
  });
}

/**
 * The same route from a video id alone — what the corner player and the queue panel point at.
 *
 * It is built rather than taken from whatever card the video arrived on: a playlist entry's own href
 * carries `list=`, and arriving with one clears the queue (`queue/useQueueAdvance.ts`), so a queue
 * row wearing a card's href would delete the list the moment it was pressed.
 */
export function queueVideoHref(videoId: string): string {
  return formatRoute({ kind: 'watch', videoId, startSec: null, playlistId: null, index: null });
}

/**
 * The queue as the watch page's side panel draws it — the panel's own row type, and the only source of
 * rows it has (`screens/watch/PlaylistPanel.tsx`): a `list` in the address makes the queue that
 * playlist, so both lists arrive here.
 *
 * `removeKey` is the entry's **queue key** whichever list it belongs to, which is why that field
 * stopped being called an `indexId`: mapping it back to the key a removal has to send is the loader's
 * job, and it is the only thing that knows both vocabularies.
 *
 * `current` follows the **cursor**, not the video id. A queue keeps a cursor precisely so that a video
 * held twice is two rows the reader can tell apart, and highlighting both would throw that away.
 *
 * **A row a playlist blanked says so**, and it is told by the one thing that makes it unrenderable: no
 * title (§6 — the endpoint blanks the row rather than omitting it, on 6.9 % of all rows, and the row is
 * kept so the numbering stays truthful). Only a linked row can be in that state; a row the reader put
 * in the queue off a card came with a title.
 */
export function toQueuePanelRows(input: {
  readonly state: QueueState;
  readonly progress: ProgressLookup;
}): readonly WatchPlaylistRow[] {
  const { state, progress } = input;
  const link = state.link;
  return state.entries.map((entry, index): WatchPlaylistRow => {
    const current = index === state.cursor;
    const gone = queueEntryGone(state, entry);
    // The playing row is exempt from the watched treatment for the panel's own reason: the video a
    // reader is watching right now is the last thing to fade away from them. A blanked row has no
    // video behind it to have watched at all.
    const facts = current || gone ? null : progress(entry.videoId);
    return {
      key: entry.key,
      videoId: entry.videoId,
      removeKey: entry.key,
      // The playlist's own number while the queue is a window on one, so a run that starts at entry
      // 350 numbers its rows 351 up rather than from one.
      position: entry.playlistIndex === null ? index + 1 : entry.playlistIndex + 1,
      href: queueEntryHref(entry, link),
      title: gone ? text().playlist.page.entryGone : entry.title,
      author: entry.author,
      authorHref: entry.authorHref,
      thumbnailUrl: entry.thumbnailUrl,
      badge: durationBadge(entry.durationSec),
      durationSec: entry.durationSec,
      current,
      unavailable: gone,
      marker: facts === null ? null : progressMarker(facts),
      progress: facts === null ? null : toListingProgress(facts.mark),
      watched: facts !== null && isWatchedVideo(facts),
    };
  });
}

/**
 * "451 of 575", or `null` while the queue holds a list nothing is playing from.
 *
 * Both numbers are the **playlist's** while the queue is a window on one: the row's own position in it
 * and the length the header declared, so a panel showing four of 575 rows says where in the list the
 * reader is rather than where in the loaded run. A list that declares no length — a YouTube mix carries
 * none (§7g) — falls back to the rows in hand, which is all a queue of the reader's own ever has.
 */
export function queuePositionText(state: QueueState): string | null {
  if (state.cursor === null) return null;
  const entry = state.entries[state.cursor];
  const at = entry?.playlistIndex ?? null;
  return text().watch.playlist.position(
    at === null ? state.cursor + 1 : at + 1,
    state.link?.videoCount ?? state.entries.length,
  );
}

/** The rows either side of the playing one, as hrefs for the panel's back and forward buttons. */
export function queuePanelNeighbours(
  state: QueueState,
  rows: readonly WatchPlaylistRow[],
): {
  readonly previous: WatchPlaylistRow | null;
  readonly next: WatchPlaylistRow | null;
} {
  const { previous, next } = queueNeighbours(state);
  const find = (entry: QueueEntry | null): WatchPlaylistRow | null =>
    entry === null ? null : (rows.find((row) => row.key === entry.key) ?? null);
  return { previous: find(previous), next: find(next) };
}

/**
 * The badge a queue row wears: its duration, or nothing.
 *
 * Never `live` and never `upcoming` — see {@link toQueueVideo} for why a queue keeps no badge of its
 * own. A stored duration of `0` is the endpoint's way of saying it did not have one, so it draws
 * nothing rather than "0:00".
 */
function durationBadge(durationSec: number | null): ListingBadge | null {
  if (durationSec === null || durationSec <= 0) return null;
  return { kind: 'duration', text: formatDuration(durationSec), title: null };
}
